import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import Filters from 'src/components/Filters';
import Title from 'src/components/PageHeader/Title';
import Header from 'src/components/PageHeader/Header';
import { InboxCountType, InboxList } from 'src/store/modules/inbox/types';
import { FilterCondition, Form, Order } from 'src/store/modules/application/types';
import InboxInfiniteList from './InboxInfiniteList';
import { extractFormData } from 'src/components/FormDisplay/FormDisplay';

type InboxProps = {
    inboxList: InboxList;
    isLoading: boolean;
    setError: Function;
    hasMore: boolean;
    inboxCount: InboxCountType;
    inboxSearchForm: Form;
    isInboxSearchFormLoading: boolean;
    getInboxSearchForm: Function;
    applyInboxFilters: Function;
    filterSelections: Record<string, string | string[]>;
    setFilterSelections: Function;
    updateInboxStatus: Function;
};

const Inbox: FunctionComponent<InboxProps> = (props) => {
    const [skip, setSkip] = useState<number>(0);
    const limit = 20;

    const {
        inboxList,
        inboxCount,
        isLoading,
        hasMore,
        inboxSearchForm,
        isInboxSearchFormLoading,
        getInboxSearchForm,
        applyInboxFilters,
        filterSelections,
        setFilterSelections,
        updateInboxStatus,
    } = props;

    const [inboxSortOrder, setInboxSortOrder] = useState<Order>('asc');
    const [inboxSortColumn, setInboxSortColumn] = useState<string>('submissionDate');
    const [listFilters, setListFilters] = useState<FilterCondition[]>([]);

    useEffect(() => {
        getInboxSearchForm();
    }, [getInboxSearchForm]);

    const refreshInboxList = useCallback(
        (listFilters, apiProps) => {
            // check if filter selections are applied before refreshing results
            if (
                filterSelections &&
                (Object.entries(filterSelections).length === 0 ||
                    (Object.entries(filterSelections).length > 0 && listFilters.length > 0))
            )
                applyInboxFilters(listFilters, {
                    limit,
                    orderBy: inboxSortOrder,
                    orderCol: inboxSortColumn,
                    ...apiProps,
                });
        },
        [applyInboxFilters, limit, inboxSortOrder, inboxSortColumn, filterSelections],
    );

    useEffect(() => {
        refreshInboxList(listFilters, { skip });
        // The disable-next-line below is to avoid redundant callbacks due to updates in listFilters
        //eslint-disable-next-line
    }, [refreshInboxList, skip]);

    useEffect(() => {
        refreshInboxList(listFilters, { skip: 0 });
        setSkip(0);
    }, [refreshInboxList, listFilters]);

    const updateInboxSortBy = (sortBy: string) => {
        setInboxSortColumn(sortBy);
        setSkip(0);
    };
    const updateInboxSortOrder = (sortOrder: Order) => {
        setInboxSortOrder(sortOrder);
        setSkip(0);
    };

    const defaultFilterSelections = inboxSearchForm && extractFormData({ status: ['Submitted'] }, inboxSearchForm);
    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid container md={2} lg={2} direction="row" alignItems="center">
                    <Title titleText="Inbox" />
                </Grid>
                <Grid item md={10} lg={10}>
                    <Filters
                        searchForm={inboxSearchForm}
                        isLoading={isInboxSearchFormLoading}
                        setListFilters={setListFilters}
                        selections={filterSelections || defaultFilterSelections}
                        handleFilterSelections={setFilterSelections}
                    />
                </Grid>
            </Header>
            <div className="verticalFillContent">
                <InboxInfiniteList
                    inboxCount={inboxCount}
                    inboxList={inboxList}
                    isLoading={isLoading}
                    skip={skip}
                    limit={limit}
                    setSkip={setSkip}
                    hasMore={hasMore}
                    sortBy={inboxSortColumn}
                    sortOrder={inboxSortOrder}
                    setSortOrder={updateInboxSortOrder}
                    setSortColumn={updateInboxSortBy}
                    updateInboxStatus={updateInboxStatus}
                />
            </div>
        </div>
    );
};

export default Inbox;
